import React from 'react';
import { DialogTitle } from '@radix-ui/react-dialog';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from '../ui/dialog';

import SearchInput from './searchInput';

import useTranslations from '@/hooks/useTranslation';
import SearchIcon from '@/svgs/search.svg';

type SearchBarProps<T> = {
  useSearch: (query: string) => {
    results: T[];
    isFetched: boolean;
    isFetching: boolean;
  };
  renderResult: (item: T) => React.ReactNode;
};

function SearchBar<T>({
  useSearch,
  renderResult,
}: SearchBarProps<T>): React.ReactElement {
  const { t } = useTranslations('components.header');
  const [open, setOpen] = React.useState(false);
  const [debouncedValue, setDebouncedValue] = React.useState('');

  const { results, isFetched, isFetching } = useSearch(debouncedValue);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setDebouncedValue('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <div className="flex items-center gap-[10px] bg-muted rounded-lg px-3 py-2 cursor-pointer">
          <div className="w-[24px] h-[24px] flex items-center justify-center">
            <SearchIcon className="stroke-muted-foreground" />
          </div>
          <p className="font-medium text-sm text-muted-foreground">
            {`${t('search')} ...`}
          </p>
        </div>
      </DialogTrigger>
      <DialogContent className="translate-y-0 [&>button]:hidden top-20 md:top-36 min-w-[100%] min-[480px]:min-w-0 border-none focus:border-none px-5 min-[480px]:px-9 py-0 bg-transparent shadow-none">
        <DialogHeader>
          <DialogTitle>
            <SearchInput
              debouncedValue={debouncedValue}
              setDebouncedValue={setDebouncedValue}
            />
          </DialogTitle>
        </DialogHeader>
        {debouncedValue && (isFetching || isFetched) && (
          <div className=" flex flex-col justify-center items-center bg-white  h-max rounded-lg min-h-24 gap-6 h max-h-60 overflow-auto ">
            {isFetching && (
              <div className="w-6 h-6 rounded-full border-2 border-muted border-t-primary animate-spin" />
            )}
            {!isFetching && isFetched && results.length === 0 && (
              <p className="text-lg">{t('notFound')}</p>
            )}
            {!isFetching && results.length > 0 && (
              <div className="p-4 flex flex-col gap-6 h-max w-full max-h-60 overflow-auto">
                {results.map((item, index) => (
                  <React.Fragment key={index}>
                    {renderResult(item)}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SearchBar;
